import React from 'react'
import styled from '@emotion/styled'
import { isErrored, isLoading, State } from 'rwwa-data-access'
import { observe } from '@core/Components/HOCs/observe'
import { CardTitleStyled } from '../Common/GiddyUp.styles'
import {
  GiddyUpQuaddieTips,
  IQuaddieLeg,
  IQuaddieRecord,
} from '../../../../data-access/giddyUpQuaddieTips'
import { getRaceDetail, isSelectQuaddieAllowed } from '../../../../integration'
import { IQuaddiePageStateRecord, quaddie$ } from '../../../../states/quaddie'
import { SelectQuaddie, ChangeQuaddieValue } from '../../../../states/signals'
import { LoadingMessage } from '../../../Common/LoadingMessage'
import { RatingStar } from '../../../Common/RatingStar'

const state$ = Rx.Observable.combineLatest(
  GiddyUpQuaddieTips.data$,
  quaddie$,
  (quaddieTips, quaddiePageState) => ({ quaddieTips, quaddiePageState })
)

export const QuaddieTips = observe<
  { quaddieTips: State<IQuaddieRecord>; quaddiePageState: IQuaddiePageStateRecord },
  { fixtureKey: string }
>(state$, ({ quaddieTips, quaddiePageState, fixtureKey }) => {
  const quaddie = fixtureKey && GiddyUpQuaddieTips.getFrom(quaddieTips, fixtureKey)

  if (isLoading(quaddie)) {
    return <LoadingMessage message='Loading' />
  }

  if (!quaddie || isErrored(quaddie)) {
    return <div data-tid-quaddie-tips>Sorry but there are no Quaddie Tips for this meeting</div>
  }

  const raceNumbers = quaddie.legs.map(x => x && x.raceNumber).toArray() as number[]
  const acceptorsStandard = quaddie.legs
    .map(x => x && x.selections.split(',').map(s => parseInt(s, 10)))
    .toArray() as number[][]
  const acceptorsValue = quaddie.legs
    .map(x => x && x.valueSelections.split(',').map(s => parseInt(s, 10)))
    .toArray() as number[][]

  const firstLegDetail = getRaceDetail()
  const showQuickbet =
    isSelectQuaddieAllowed() && firstLegDetail ? firstLegDetail.raceStatus === 'Open' : false

  return (
    <QuaddieTipsStyled data-tid-quaddie-tips>
      <div className='l-faux-table w100 p1em'>
        <div data-tid-heading className='l-faux-row w100'>
          Race {Math.min(...raceNumbers)} - Race {Math.max(...raceNumbers)}
        </div>

        <div className='l-faux-row w100' data-tid-quaddie-confidence>
          <div className='l-faux-cell pb1em l-cell-xs-12'>
            <div className='l-faux-table w100 pthalfem'>
              <div className='l-faux-row w100'>
                <div
                  className='l-faux-cell l-cell-xs-6'
                  style={{ fontWeight: 'bold', verticalAlign: 'middle' }}
                >
                  Quaddie Confidence:
                </div>
                <div className='l-faux-cell l-cell-xs-5 alignmid text-right pr10'>
                  <RatingStar raceConfidence={quaddie.confidence} />
                </div>
                <div
                  className='l-faux-cell l-cell-xs-1'
                  style={{
                    fontWeight: 'bold',
                    verticalAlign: 'middle',
                    color: '#552d6b',
                    fontSize: '1.6em',
                  }}
                >
                  {`${quaddie.confidence}%`}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='l-faux-row' data-tid-quaddie-comment>
          <span
            className='faux-strong db fz-12 pthalfem'
            style={{ borderTop: '1px solid #cecece' }}
          >
            Overview
          </span>
          {quaddie.comment}
        </div>
      </div>

      <div className='l-faux-table l-table-padded w100'>
        <CardTitleStyled className='l-faux-row fz-12'>
          <div className='l-faux-cell l-cell-xs-3 alignmid pl1em prhalfem faux-strong'>Leg</div>
          <div className='l-faux-cell l-cell-xs-7 alignmid plhalfem'>Selection No.</div>
          <div className='l-faux-cell l-cell-xs-2 alignmid pr1em text-right'>
            <button
              type='button'
              className={`nueSecondary valueButton nue-button fz-11 ${quaddiePageState.valueSelected ? 'valueButtonSelected' : ''}`}
              onClick={ChangeQuaddieValue}
            >
              Value
            </button>
          </div>
        </CardTitleStyled>
      </div>

      <div className='l-faux-table w100 pbhalfem'>
        {quaddie.legs
          .toArray()
          .map(
            (leg, index) =>
              leg && (
                <Leg
                  key={leg.raceNumber}
                  index={index || 0}
                  leg={leg}
                  isValueQuaddie={quaddiePageState.valueSelected}
                />
              )
          )}
      </div>

      <div data-tid-quaddie-outlay className='quaddieOutlay card w100 pthalfem pbhalfem'>
        <div className='fz-14 quaddieOutlayHeader'>Outlay</div>
        {quaddiePageState.valueSelected ? quaddie.valueOutlay : quaddie.outlay}
      </div>

      {showQuickbet && (
        <div className='l-faux-table l-table-fixed w100 quaddieBetHolder'>
          <button
            type='button'
            className='nueSecondary valueButton bg-ui-good fg-gray-lightest nue-button nue-block fz-12 text-orig p1em'
            onClick={() =>
              SelectQuaddie({
                raceNumbers,
                acceptorNumbers: quaddiePageState.valueSelected
                  ? acceptorsValue
                  : acceptorsStandard,
              })
            }
          >
            <strong>Quickbet</strong>
          </button>
        </div>
      )}
    </QuaddieTipsStyled>
  )
})

// ================
// Local Components
// ================

const Leg: React.FC<{
  leg: IQuaddieLeg
  index: number
  isValueQuaddie: boolean
}> = ({ leg, index, isValueQuaddie }) => {
  return (
    <div data-tid-quaddie-leg={index} className='l-faux-row w100 card'>
      <div className='l-faux-cell l-cell-xs-3 pl1em'>
        <div data-tid-quaddie-leg-ordinal className='faux-strong fz-14'>
          {ordanalise(index + 1)}
        </div>
        <div data-tid-quaddie-leg-race>Race {leg.raceNumber}</div>
      </div>
      <div className='l-faux-cell l-cell-xs-9 pbhalfem pthalfem plhalfem pr1em'>
        <div data-tid-quaddie-leg-selections className='faux-strong fz-14'>
          {isValueQuaddie ? (
            <FormattedSelections selections={leg.valueSelections} />
          ) : (
            <FormattedSelections selections={leg.selections} />
          )}
        </div>
        <div data-tid-quaddie-leg-comment> {leg.comment} </div>
      </div>
    </div>
  )
}

const FormattedSelections: React.FC<{ selections: string }> = ({ selections }) =>
  selections.split(',').map((selectionNumber, index) => (
    <span key={index} className='quaddieSelection'>
      {selectionNumber}
    </span>
  ))

// =============
// Local Helpers
// =============

function ordanalise(value: number) {
  switch (value) {
    case 1:
      return '1st'
    case 2:
      return '2nd'
    case 3:
      return '3rd'
    default:
      return `${value}th`
  }
}

// ======
// Styles
// ======

const QuaddieTipsStyled = styled.div({
  maxWidth: '64em',
  margin: '0 auto',

  '> div:first-of-type': {
    boxShadow: '0 2px 2px -2px rgba(0, 0, 0, 0.4)',
    backgroundColor: '#fff',
    marginBottom: 1,

    '> div:first-of-type': {
      color: '#333',
      fontSize: '1.5em',
      fontWeight: 'bold',
      verticalAlign: 'middle',
    },
  },

  '.quaddieOutlayHeader': {
    fontWeight: 'bold',
  },

  '.quaddieOutlay': {
    backgroundColor: '#a9a9a9',
    textAlign: 'center',
    color: '#fff',
    marginBottom: '6em',
  },

  '.quaddieSelection': {
    marginRight: '0.8em',
  },

  '.nueSecondary': {
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #eee',
  },

  '.valueButton': {
    borderColor: '#ccc',
    marginBottom: 0,
    margin: '0.5rem 0',
  },

  '.valueButtonSelected': {
    backgroundColor: '#ff9600',
    color: '#fff',
  },

  '.quaddieBetHolder': {
    bottom: 0,
    transform: 'translate(0, 0)',
    width: '100%',
    position: 'fixed',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 1,
    padding: '0.5em',
    maxWidth: '64em',
  },
})
